'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  buildApiUrl,
  API_ENDPOINTS,
  getAuthToken,
  removeAuthToken
} from '@/config/api';

interface User {
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  refetch: async () => {}
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // 初始值设为 true，表示正在检查认证状态
  const [authError, setAuthError] = useState(false); // true 表示服务端明确返回 401，其他网络错误不视为 auth 错误
  const pathname = usePathname();
  const router = useRouter();
  const loginRedirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 判断是否需要获取用户信息
  const shouldFetchUser = () => {
    // 在根路径不需要获取（会重定向）
    if (pathname === '/') return false;
    // 在登录页面和受保护页面都需要获取（登录页面需要检查是否已登录）
    return pathname.startsWith('/auth/') || pathname.startsWith('/dashboard');
  };

  const fetchUser = async () => {
    if (!shouldFetchUser()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setAuthError(false);
    try {
      const token = typeof window !== 'undefined' ? getAuthToken() : null;

      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const apiUrl = buildApiUrl(API_ENDPOINTS.PROFILE);

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        console.log('Profile API response:', data); // 调试日志

        // 根据API响应格式提取用户信息
        if (data.code === 0 && data.data && data.data.admin) {
          setUser(data.data.admin);
        } else if (data.admin) {
          setUser(data.admin);
        } else if (data.user) {
          setUser(data.user);
        } else {
          console.warn('无法从响应中提取用户信息:', data);
          setUser(null);
          // 业务码非 0 但 HTTP 200，按未授权处理（避免用无效 token 反复请求）
          setAuthError(true);
        }
      } else {
        setUser(null);
        // 只有服务端明确返回 401 才认为是 token 失效
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error: any) {
      // 网络错误、超时、API 重启等，不清 token，只把 user 置空
      setUser(null);
      setAuthError(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // 当在受保护页面但无法获取用户信息时，重定向到登录页
  // 当在登录页面但已有有效用户信息时，重定向到 dashboard
  useEffect(() => {
    // 如果正在加载中，不执行重定向
    if (loading) return;

    // 如果在登录页面但已经有有效的用户信息，重定向到 dashboard
    if (pathname.startsWith('/auth/') && user) {
      router.push('/dashboard/home');
      router.refresh();
      return;
    }

    // 如果在受保护的 dashboard 页面，但用户为 null，则重定向到登录页
    // 这包括两种情况：
    // 1. 没有 token
    // 2. 有 token 但 API 请求失败（token 过期或无效）
    if (pathname.startsWith('/dashboard') && !user) {
      // 检查是否有 token，如果有 token 但用户为 null，说明 token 可能无效
      const token = typeof window !== 'undefined' ? getAuthToken() : null;

      // 检查是否是登录后的跳转（通过 URL 参数判断）
      const isLoginRedirect = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('_login') === '1'
        : false;

      // 如果是登录后的跳转，且有 token，给更多时间让 fetchUser 完成
      // 这可以避免在静态导出环境下，页面刚加载时 token 还未被读取的问题
      if (isLoginRedirect && token) {
        // 清除之前的 timeout（如果存在）
        if (loginRedirectTimeoutRef.current) {
          clearTimeout(loginRedirectTimeoutRef.current);
        }

        // 延迟检查，给 fetchUser 更多时间
        // 如果 fetchUser 成功，user 会被设置，useEffect 会重新执行，不会进入这个分支
        loginRedirectTimeoutRef.current = setTimeout(() => {
          // 检查 token 是否还存在
          const currentToken = typeof window !== 'undefined' ? getAuthToken() : null;
          if (!currentToken) {
            router.push('/auth/sign-in');
            router.refresh();
            return;
          }

          // 再次尝试获取用户信息
          fetchUser().catch(() => {
            // 如果获取失败，清除 token 并重定向
            removeAuthToken();
            router.push('/auth/sign-in');
            router.refresh();
          });
        }, 2000); // 等待2秒，给 API 请求足够的时间

        return () => {
          if (loginRedirectTimeoutRef.current) {
            clearTimeout(loginRedirectTimeoutRef.current);
            loginRedirectTimeoutRef.current = null;
          }
        };
      }

      // 如果不是登录后的跳转，或者没有 token，立即处理
      if (!isLoginRedirect || !token) {
        if (!token) {
          // 没有 token，直接重定向
          router.push('/auth/sign-in');
          router.refresh();
        } else if (authError) {
          // 有 token 但服务端明确返回 401，清除后重定向
          removeAuthToken();
          router.push('/auth/sign-in');
          router.refresh();
        } else {
          // 有 token 但只是网络/超时/API 重启等临时失败，不重定向也不清 token
          // 页面会停留在当前路由，API 恢复后刷新即可恢复登录态
          console.warn('认证接口临时不可用，保留本地 token');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, pathname, authError]);

  return (
    <AuthContext.Provider value={{ user, loading, refetch: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
